import type { EventingMetadata } from '../types/normalized';

const MAX_METADATA_TEXT_LENGTH = 200;

const ACTIONABLE_SELECTOR =
  'a[href], button, [role="button"], input, select, textarea, label';

const HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6';

/**
 * Card-like `data-hen-id` hosts: use the first heading as `text` (title), not
 * the full card body, unless the user activated a nested link/button.
 */
const CARD_TITLE_COMPONENT_IDS = new Set([
  'hen.card',
  'hen.articleCard',
  'hen.imageCard',
  'hen.infoCard',
  'hen.brandCard',
  'hen.degreeCardDetailed',
  'hen.degreeCardSimple',
  'hen.degreeCardTiny',
]);

function truncate(value: string): string {
  return value.length <= MAX_METADATA_TEXT_LENGTH
    ? value
    : value.slice(0, MAX_METADATA_TEXT_LENGTH);
}

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function readAssociatedLabelText(element: Element): string | undefined {
  const labelledBy = element.getAttribute('aria-labelledby');
  if (labelledBy) {
    const doc = element.ownerDocument;
    if (doc) {
      const parts = labelledBy
        .split(/\s+/)
        .map((id) => {
          const labelEl = doc.getElementById(id);
          return labelEl?.innerText || labelEl?.textContent || '';
        })
        .join(' ');
      const normalized = normalizeWhitespace(parts);
      if (normalized) {
        return normalized;
      }
    }
  }

  if (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement
  ) {
    const id = element.id;
    if (id && element.ownerDocument) {
      const doc = element.ownerDocument;
      const label =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? doc.querySelector<HTMLLabelElement>(
              `label[for="${CSS.escape(id)}"]`,
            )
          : Array.from(doc.getElementsByTagName('label')).find(
              (candidate) => candidate.htmlFor === id,
            );
      const labelText = label?.innerText || label?.textContent;
      if (labelText) {
        const normalized = normalizeWhitespace(labelText);
        if (normalized) {
          return normalized;
        }
      }
    }
  }

  return undefined;
}

function extractVisibleText(element: Element): string | undefined {
  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel) {
    const normalized = normalizeWhitespace(ariaLabel);
    if (normalized) {
      return truncate(normalized);
    }
  }

  const labelText = readAssociatedLabelText(element);
  if (labelText) {
    return truncate(labelText);
  }

  if (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement
  ) {
    const placeholder = element.getAttribute('placeholder');
    if (placeholder) {
      const normalized = normalizeWhitespace(placeholder);
      if (normalized) {
        return truncate(normalized);
      }
    }
    return undefined;
  }

  const rawText =
    (element as HTMLElement).innerText || (element as HTMLElement).textContent;
  if (rawText) {
    const normalized = normalizeWhitespace(rawText);
    if (normalized) {
      return truncate(normalized);
    }
  }

  return undefined;
}

function resolveMetadataElement(
  host: Element,
  target: EventTarget | null,
): Element {
  if (!(target instanceof Element) || !host.contains(target)) {
    return host;
  }

  const actionable = target.closest(ACTIONABLE_SELECTOR);
  if (actionable && host.contains(actionable)) {
    return actionable;
  }

  return target;
}

function isNestedActionable(host: Element, metadataElement: Element): boolean {
  if (metadataElement === host) {
    return false;
  }

  const actionable = metadataElement.closest(ACTIONABLE_SELECTOR);
  return Boolean(
    actionable &&
      actionable !== host &&
      host.contains(actionable) &&
      (actionable === metadataElement || actionable.contains(metadataElement)),
  );
}

function extractCardTitleText(host: Element): string | undefined {
  const heading = host.querySelector(HEADING_SELECTOR);
  if (!heading) {
    return undefined;
  }
  return extractVisibleText(heading);
}

/**
 * Visible label for Tagular `webElement.text` (never `href` or input values).
 *
 * - `hen.link` / `hen.button`: label on the `<a>` or `<button>` (or aria-label).
 * - Card-like hosts: first heading (title), unless the click target is a nested CTA.
 */
function extractMetadataText(
  host: Element,
  metadataElement: Element,
): string | undefined {
  const componentId = host.getAttribute('data-hen-id');
  if (
    componentId &&
    CARD_TITLE_COMPONENT_IDS.has(componentId) &&
    !isNestedActionable(host, metadataElement)
  ) {
    const title = extractCardTitleText(host);
    if (title) {
      return title;
    }
  }

  return extractVisibleText(metadataElement);
}

/**
 * Explicit allowlist of passive `data-*` attributes eventing may read off the
 * DOM. Anything not listed here is ignored — consumers cannot smuggle arbitrary
 * attributes into the beam. Keep values bounded via {@link truncate}.
 */
const HEN_DATA_PREFIX = 'data-hen-';
const PASSIVE_METADATA_SUFFIXES = ['location', 'name'] as const;

function readPassiveTrackingAttr(
  metadataElement: Element,
  host: Element,
  suffix: (typeof PASSIVE_METADATA_SUFFIXES)[number],
): string | undefined {
  const attrName = `${HEN_DATA_PREFIX}${suffix}`;
  const fromMetadata = metadataElement.getAttribute(attrName)?.trim();
  if (fromMetadata) {
    return truncate(fromMetadata);
  }
  const fromHost = host.getAttribute(attrName)?.trim();
  return fromHost ? truncate(fromHost) : undefined;
}

function buildMetadataFromHost(
  host: Element,
  metadataElement: Element,
): EventingMetadata | undefined {
  const metadata: Record<string, string> = {};

  const text = extractMetadataText(host, metadataElement);
  if (text) {
    metadata.text = text;
  }

  const htmlId = metadataElement.id?.trim() || host.id?.trim();
  if (htmlId) {
    metadata.htmlId = htmlId;
  }

  for (const suffix of PASSIVE_METADATA_SUFFIXES) {
    const value = readPassiveTrackingAttr(metadataElement, host, suffix);
    if (value) {
      metadata[suffix] = value;
    }
  }

  return Object.keys(metadata).length > 0 ? metadata : undefined;
}

export function extractViewMetadataFromElement(
  host: Element,
): EventingMetadata | undefined {
  return buildMetadataFromHost(host, host);
}

export function extractInteractionMetadataFromElement(
  host: Element,
  target: EventTarget | null,
): EventingMetadata | undefined {
  const metadataElement = resolveMetadataElement(host, target);
  return buildMetadataFromHost(host, metadataElement);
}
